import { callApi } from './api';
import Cohort, { CohortStatus, LiveSession } from '@/models/cohort';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const cohortsAdminApiUrl = import.meta.env.VITE_API_URL_V1 + '/cohorts/admin/cohorts';

export const getCohorts = async () => {
    return callApi<Cohort[]>({ url: cohortsAdminApiUrl, method: 'GET' });
};

export const useCohorts = () => {
    return useQuery({ queryKey: ['cohorts'], queryFn: getCohorts });
};

export const getCohort = async (cohortId: string) => {
    return callApi<Cohort>({ url: `${cohortsAdminApiUrl}/${cohortId}`, method: 'GET' });
};

export const useCohort = (cohortId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['cohort', cohortId],
        queryFn: () => getCohort(cohortId),
        enabled: !!cohortId && isOpen,
    });
};

/**
 * Only editable fields go over the wire (the API's validate() rejects unmatched
 * body keys). `currentAttendeeCount` is server-maintained and never sent.
 */
export type CohortMutationPayload = {
    courseId: string;
    title: string;
    description?: string;
    instructorId?: string;
    startDate: string;
    endDate?: string;
    joinFormUrl?: string;
    timezone: string;
    attendeeCapacity: number;
    status: CohortStatus;
    liveSessions?: LiveSession[];
};

export const createCohort = async (payload: CohortMutationPayload) => {
    return callApi<Cohort>({ url: cohortsAdminApiUrl, method: 'POST', data: payload });
};

export const useCreateCohort = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createCohort,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        },
    });
};

export const updateCohort = async ({ cohortId, ...payload }: CohortMutationPayload & { cohortId: string }) => {
    return callApi<Cohort>({ url: `${cohortsAdminApiUrl}/${cohortId}`, method: 'PUT', data: payload });
};

export const useUpdateCohort = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateCohort,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
            queryClient.invalidateQueries({ queryKey: ['cohort', variables.cohortId] });
        },
    });
};

export const deleteCohort = async ({ cohortId }: { cohortId: string }) => {
    return callApi({ url: `${cohortsAdminApiUrl}/${cohortId}`, method: 'DELETE' });
};

export const useDeleteCohort = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteCohort,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        },
    });
};

/* ─────────────────────────── members (roster) ─────────────────────────── */

// A cohort member as the admin picker sees it: the user's id + email. Adding a
// member enrols them AND grants a year of Premium + Course 33 (server-side).
export interface CohortMember {
    userId: string;
    email: string;
}

export const getCohortMembers = async (cohortId: string) => {
    return callApi<{ members: CohortMember[] }>({ url: `${cohortsAdminApiUrl}/${cohortId}/members`, method: 'GET' });
};

export const addCohortMember = async (cohortId: string, userId: string) => {
    return callApi<CohortMember>({ url: `${cohortsAdminApiUrl}/${cohortId}/members`, method: 'POST', data: { userId } });
};

export const removeCohortMember = async (cohortId: string, userId: string) => {
    return callApi({ url: `${cohortsAdminApiUrl}/${cohortId}/members/${userId}`, method: 'DELETE' });
};
